import { FC } from 'react';
import { QueryResult } from '@apollo/client';
import { Flex, useToast } from '@chakra-ui/react';
import { useDownloadFile } from '@resolutionlife/ui';
import { isEmpty } from 'ramda';

import * as ToastConfigs from '../../configs/toast';
import {
  DownloadFileQueryVariables,
  GetSearchResultsQuery,
  useDownloadFileQuery,
} from '../../graphql/generated';
import { useSearchPagination } from '../../hooks/use-search-pagination/use-search-pagination';
import { DebounceLoadingState } from '../../ui/debounce-loading/debounce-loading';
import { EmptyState } from '../../ui/errors/empty-state';
import { ErrorState } from '../../ui/errors/error-state';
import { LoadingState } from '../file-browser';
import { Pagination } from '../pagination/pagination';
import { SearchResultsList } from './search-results-list';

type SearchResultsContainerProps = {
  searchTerm: string;
  getSearchResults: Pick<
    QueryResult<GetSearchResultsQuery>,
    'error' | 'loading' | 'data' | 'refetch'
  >;
};

export const SearchResults: FC<SearchResultsContainerProps> = ({
  searchTerm,
  getSearchResults,
}) => {
  const toast = useToast();
  const { download, downloadRef } = useDownloadFile();
  const { refetch: generateDownloadUrl } = useDownloadFileQuery({ skip: true });
  const pagination = useSearchPagination({
    results: getSearchResults.data?.search.objects || [],
    count: getSearchResults.data?.search.count || 0,
  });

  const { error, loading, data } = getSearchResults;

  if (error) {
    return (
      <ErrorState
        title="An error occurred while performing your search"
        subtitle="Please reach out to the technical support team for further assistance."
      />
    );
  }

  if (loading) {
    return (
      <Flex flex="1" mt="4" data-testid="loading-state">
        <DebounceLoadingState>
          <LoadingState />
        </DebounceLoadingState>
      </Flex>
    );
  }

  if (isEmpty(searchTerm)) {
    return (
      <EmptyState
        title="Search"
        subtitle="Please enter a valid search term in the search bar above"
      />
    );
  }

  if (!data?.search.objects) {
    return (
      <EmptyState
        title="No results found"
        subtitle="Try broadening your search term or remove any active filters"
      />
    );
  }

  const handleFileDownload = async (file: DownloadFileQueryVariables) => {
    try {
      const {
        data: { cedant },
      } = await generateDownloadUrl({
        cedant: file.cedant,
        fileId: file.fileId,
      });

      if (cedant?.signedUrl) {
        download(downloadRef, cedant.signedUrl);
      }
    } catch {
      toast(ToastConfigs.problemCompletingRequest);
    }
  };

  return (
    <>
      <SearchResultsList
        downloadRef={downloadRef}
        files={data.search.objects}
        onDownload={handleFileDownload}
        EmptyState={
          <EmptyState
            title="No results found"
            subtitle="Try broadening your search term or remove any active filters"
          />
        }
      />
      {pagination.totalItemCount > 0 && (
        <Pagination
          pageSize={pagination.pageSize}
          data-testid="pagination-controls"
          total={pagination.totalItemCount}
          current={pagination.currentPageNumber}
          onChange={pagination.handlePageChange}
        />
      )}
    </>
  );
};
